import React, { useState, useEffect } from 'react';
import { CATEGORIES, SOUS_CATEGORIES } from '../data/categories';
import { X, Save, Trash2, Camera, MapPin, Package, Tag, Info } from 'lucide-react';
import ColorSelector from './ColorSelector';
import PhotoUpload from './PhotoUpload';
import LocationDisplay from './LocationDisplay';
import LocationSelector from './LocationSelector';
import MovementHistory from './MovementHistory';
import Switch from './Switch';
import { generateSKU } from '../utils/skuGenerator';
import { usePermissions } from '../hooks/usePermissions';

interface ObjetEditModalProps {
  objet: any;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedObjet: any, locationChanged: { hasChanged: boolean; originalZoneId?: string; originalBoxId?: string }) => void;
  onDelete?: (objetId: string) => void;
}

const ObjetEditModalMobile: React.FC<ObjetEditModalProps> = ({ 
  objet, 
  isOpen, 
  onClose, 
  onSave, 
  onDelete 
}) => {
  const permissions = usePermissions();

  const [activeSection, setActiveSection] = useState<'infos' | 'localisation' | 'photos' | 'historique'>('infos');
  const [formData, setFormData] = useState({
    nom: '',
    categorie: '',
    sousCategorie: '',
    quantite: 1,
    etat: 'bon',
    besoinAchat: false,
    remarques: '',
    sku: '',
    couleurPrincipale: '',
    couleurSecondaire: '',
    zoneId: '',
    boxId: '',
  });

  const [photos, setPhotos] = useState<string[]>([]);
  const [originalLocation, setOriginalLocation] = useState<{ zoneId?: string; boxId?: string }>({});

  useEffect(() => {
    if (objet) {
      const initialData = {
        nom: objet.nom || '',
        categorie: objet.categorie || '',
        sousCategorie: objet.sousCategorie || '',
        quantite: objet.quantite || 1,
        etat: objet.etat || 'bon',
        besoinAchat: objet.besoinAchat || false,
        remarques: objet.remarques || '',
        sku: objet.sku || '',
        couleurPrincipale: objet.couleurPrincipale || '',
        couleurSecondaire: objet.couleurSecondaire || '',
        zoneId: objet.zoneId || '',
        boxId: objet.boxId || '',
      };

      setFormData(initialData);
      setPhotos(objet.photos || []);
      setOriginalLocation({ zoneId: objet.zoneId, boxId: objet.boxId });
    }
  }, [objet]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Détecter si la localisation a changé
    const hasChangedLocation = 
      formData.zoneId !== originalLocation.zoneId || 
      formData.boxId !== originalLocation.boxId;

    // Passer les données ET les informations de changement de localisation au parent
    onSave(
      {
        ...objet,
        ...formData,
        photos: photos,
      },
      {
        hasChanged: hasChangedLocation,
        originalZoneId: originalLocation.zoneId,
        originalBoxId: originalLocation.boxId,
      }
    );
  };

  const handleDelete = () => {
    if (window.confirm(`Êtes-vous sûr de vouloir supprimer "${objet.nom}" ?`)) {
      onDelete?.(objet.id);
      onClose();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (name === 'categorie') {
      setFormData(prev => ({ 
        ...prev, 
        [name]: value,
        sousCategorie: ''
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
      }));
    }
  };

  const handleColorChange = (field: 'couleurPrincipale' | 'couleurSecondaire', value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleGenerateSKU = () => {
    if (formData.nom && formData.categorie) {
      const generatedSKU = generateSKU({
        categorie: formData.categorie,
        sousCategorie: formData.sousCategorie,
        nom: formData.nom,
        zoneId: formData.zoneId,
      });
      setFormData(prev => ({ ...prev, sku: generatedSKU }));
    }
  };

  if (!isOpen) return null;

  const sections = [
    { id: 'infos' as const, label: 'Infos', icon: Info },
    { id: 'localisation' as const, label: 'Lieu', icon: MapPin },
    { id: 'photos' as const, label: 'Photos', icon: Camera },
    { id: 'historique' as const, label: 'Déplacements', icon: Package },
  ];

  return (
    <div className="fixed inset-0 z-50 overflow-hidden bg-black bg-opacity-50 flex items-end sm:items-center sm:justify-center">
      {/* Modal Container - Mobile first avec slide-up animation */}
      <div className="w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-2xl bg-white sm:rounded-t-2xl sm:rounded-b-2xl flex flex-col animate-slide-up">
        
        {/* Header - Sticky */}
        <div className="flex-shrink-0 sticky top-0 z-10 bg-white border-b border-gray-200 px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate flex-1 pr-2">
              {permissions.canEdit ? 'Modifier l\'objet' : 'Détails de l\'objet'}
            </h2>
            <button
              onClick={onClose}
              className="flex-shrink-0 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
          </div>

          {/* Navigation par onglets - Mobile optimisé */}
          <div className="mt-3 flex space-x-1 overflow-x-auto scrollbar-hide">
            {sections.map((section) => {
              const Icon = section.icon;
              return (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`flex items-center space-x-1.5 px-3 sm:px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                    activeSection === section.id
                      ? 'bg-blue-600 text-white shadow-md'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-sm font-medium">{section.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          <form onSubmit={handleSubmit} id="objet-form">
            
            {/* Section Informations */}
            {activeSection === 'infos' && (
              <div className="space-y-4">
                {/* Nom - Input mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Nom de l'objet *
                  </label>
                  <input
                    type="text"
                    name="nom"
                    value={formData.nom}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ex: Ballon de football"
                  />
                </div>

                {/* Catégorie - Select mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Catégorie *
                  </label>
                  <select
                    name="categorie"
                    value={formData.categorie}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    required
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option key="empty-categorie" value="">Sélectionner une catégorie</option>
                    {CATEGORIES.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                {/* Sous-catégorie */}
                {formData.categorie && SOUS_CATEGORIES[formData.categorie] && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Sous-catégorie
                    </label>
                    <select
                      name="sousCategorie"
                      value={formData.sousCategorie}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option key="empty-souscategorie" value="">Sélectionner une sous-catégorie</option>
                      {SOUS_CATEGORIES[formData.categorie].map(subcat => (
                        <option key={subcat.value} value={subcat.value}>{subcat.label}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Quantité et État - Grid responsive */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      Quantité *
                    </label>
                    <input
                      type="number"
                      name="quantite"
                      value={formData.quantite}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      min="0"
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1.5">
                      État *
                    </label>
                    <select
                      name="etat"
                      value={formData.etat}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      required
                      className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      <option key="bon" value="bon">Bon</option>
                      <option key="a_reparer" value="a_reparer">À réparer</option>
                      <option key="incomplet" value="incomplet">Incomplet</option>
                      <option key="manquant" value="manquant">Manquant</option>
                      <option key="HS" value="HS">Hors Service</option>
                      <option key="a_verifier" value="a_verifier">À vérifier</option>
                    </select>
                  </div>
                </div>

                {/* Besoin d'achat - Switch moderne et tactile */}
                <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <Switch
                    checked={formData.besoinAchat}
                    onChange={(checked) => setFormData(prev => ({ ...prev, besoinAchat: checked }))}
                    label="Besoin d'achat"
                    disabled={!permissions.canEdit}
                    size="lg"
                    color="orange"
                  />
                  <p className="text-xs text-gray-500 mt-2 ml-1">
                    {formData.besoinAchat 
                      ? '🛒 Cet objet doit être acheté' 
                      : 'Pas de besoin d\'achat actuellement'}
                  </p>
                </div>

                {/* SKU - Avec bouton de génération */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    SKU (Code unique)
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      name="sku"
                      value={formData.sku}
                      onChange={handleChange}
                      disabled={!permissions.canEdit}
                      className="flex-1 px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      placeholder="Généré automatiquement"
                    />
                    {permissions.canEdit && (
                      <button
                        type="button"
                        onClick={handleGenerateSKU}
                        className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors whitespace-nowrap"
                      >
                        <Tag className="h-5 w-5" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Couleurs - Selecteurs tactiles */}
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur principale
                    </label>
                    <ColorSelector
                      value={formData.couleurPrincipale}
                      onChange={(color) => handleColorChange('couleurPrincipale', color)}
                      disabled={!permissions.canEdit}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Couleur secondaire
                    </label>
                    <ColorSelector
                      value={formData.couleurSecondaire}
                      onChange={(color) => handleColorChange('couleurSecondaire', color)}
                      disabled={!permissions.canEdit}
                    />
                  </div>
                </div>

                {/* Remarques - Textarea mobile-optimisé */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Remarques
                  </label>
                  <textarea
                    name="remarques"
                    value={formData.remarques}
                    onChange={handleChange}
                    disabled={!permissions.canEdit}
                    rows={4}
                    className="w-full px-4 py-3 text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="Ajoutez des remarques..."
                  />
                </div>
              </div>
            )}

            {/* Section Localisation */}
            {activeSection === 'localisation' && (
              <div className="space-y-4">
                <LocationSelector
                  currentZoneId={formData.zoneId}
                  currentBoxId={formData.boxId}
                  onLocationChange={(zoneId, boxId) => {
                    setFormData(prev => ({
                      ...prev,
                      zoneId: zoneId || '',
                      boxId: boxId || '',
                    }));
                  }}
                  disabled={!permissions.canMove}
                  showCurrentLocation={true}
                />

                {(formData.zoneId !== originalLocation.zoneId || formData.boxId !== originalLocation.boxId) && permissions.canMove && (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800 font-medium flex items-center space-x-2">
                      <MapPin className="h-4 w-4" />
                      <span>Changement de localisation détecté</span>
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Ce déplacement sera enregistré dans l'historique lors de l'enregistrement.
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Section Photos */}
            {activeSection === 'photos' && (
              <div className="space-y-4">
                <PhotoUpload 
                  photos={photos} 
                  onChange={setPhotos}
                  disabled={!permissions.canEdit}
                />
              </div>
            )}

            {/* Section Historique */}
            {activeSection === 'historique' && (
              <div>
                <MovementHistory itemId={objet.id} compact />
              </div>
            )}
          </form>
        </div>

        {/* Footer - Sticky avec actions */}
        <div className="flex-shrink-0 sticky bottom-0 bg-white border-t border-gray-200 px-4 py-3 sm:px-6 space-y-2">
          {/* Boutons d'action */}
          <div className="flex space-x-2">
            {permissions.canEdit && (
              <button
                type="submit"
                form="objet-form"
                className="flex-1 flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors font-medium"
              >
                <Save className="h-5 w-5" />
                <span>Enregistrer</span>
              </button>
            )}

            {permissions.canDelete && onDelete && (
              <button
                type="button"
                onClick={handleDelete}
                className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors font-medium"
              >
                <Trash2 className="h-5 w-5" />
                <span className="sm:inline hidden">Supprimer</span>
              </button>
            )}

            <button
              type="button"
              onClick={onClose}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 active:bg-gray-400 transition-colors font-medium"
            >
              Annuler
            </button>
          </div>

          {/* Indicateur de modification de localisation */}
          {(formData.zoneId !== originalLocation.zoneId || formData.boxId !== originalLocation.boxId) && (
            <p className="text-xs text-center text-gray-600">
              💾 Ce déplacement sera enregistré dans l'historique
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObjetEditModalMobile;

